"use client"

import { useEffect, useRef, useState, type ReactNode, createContext, useContext } from "react"
import Lenis from "lenis"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

// Register GSAP plugins
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger)
}

interface SmoothScrollProviderProps {
  children: ReactNode
}

// Context for accessing Lenis instance
const LenisContext = createContext<Lenis | null>(null)
export const useLenis = () => useContext(LenisContext)

// Responsive duration based on device type
function getScrollDuration(): number {
  if (typeof window === "undefined") return 1.2
  // Mobile devices need faster scroll response for better UX
  return window.innerWidth < 768 ? 0.8 : 1.2
}

export function SmoothScrollProvider({ children }: SmoothScrollProviderProps) {
  // Use state instead of ref so context consumers get updates
  const [lenis, setLenis] = useState<Lenis | null>(null)

  useEffect(() => {
    // Initialize Lenis with responsive duration
    const lenisInstance = new Lenis({
      duration: getScrollDuration(),
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // Expo easing
      orientation: "vertical",
      gestureOrientation: "vertical",
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 2,
      infinite: false,
    })

    // Update state to provide instance to context consumers
    setLenis(lenisInstance)

    // Sync Lenis with GSAP ScrollTrigger
    lenisInstance.on("scroll", ScrollTrigger.update)

    // Use GSAP ticker for smooth animation sync
    const rafCallback = (time: number) => {
      lenisInstance.raf(time * 1000)
    }
    gsap.ticker.add(rafCallback)
    gsap.ticker.lagSmoothing(0)

    // Handle resize for responsive duration updates
    const handleResize = () => {
      const newDuration = getScrollDuration()
      if (lenisInstance.options.duration !== newDuration) {
        lenisInstance.options.duration = newDuration
      }
    }
    window.addEventListener("resize", handleResize)

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize)
      gsap.ticker.remove(rafCallback)
      lenisInstance.destroy()
      setLenis(null)
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill())
    }
  }, [])

  return <LenisContext.Provider value={lenis}>{children}</LenisContext.Provider>
}
